import { createSlice } from '@reduxjs/toolkit';
import {
  getDraftFileSession,
  saveDraftFileSession
} from '../../../lib/sessionStorage';
import { 
  BASE_END_POINT_FILE, 
  FILE_END_POINT, 
  UPLOAD_FILE_END_POINT, 
  GET_USER_FILE_END_POINT 
} from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';

export const fileSchemaInit = {
  id: '',
  userId: '',
  name: '',
  type: '',
  publicUrl: '',
  createdAt: 0,
  updatedAt: 0
};

const initialState = {
  current: fileSchemaInit,
  draft: fileSchemaInit,
  isProcessing: false,
  loading: false,
  error: null,
  fetched: false
};

const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    fileActionRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fileActionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload.error;
      state.isProcessing = false;
    },
    getCompanyLogoFileSucceeded(state, action) {
      state.current = action.payload.file;
      state.isProcessing = false;
      state.loading = false;
      state.error = null;
      state.fetched = true;
    },
    createFileForUserSucceeded(state, action) {
      saveDraftFileSession(action.payload.file);
      state.draft = action.payload.file;
      state.current = action.payload.file;
      state.isProcessing = false;
      state.loading = false;
      state.error = null;
      state.fetched = true;
    },
    fileIsStartProcessed(state) {
      state.isProcessing = true;
    },
    fileIsEndProcessed(state) {
      state.isProcessing = false;
    },
    currentFileUpdated(state, action) {
      state.current = action.payload.file;
      state.loading = false;
      state.error = null;
      state.fetched = true;
    },
    fileDeleted(state) {
      state.isProcessing = true;
    },
    fileDeletedSucceeded(state) {
      state.current = { ...fileSchemaInit };
      state.isProcessing = false;
      state.loading = false;
      state.error = null;
      saveDraftFileSession({});
      state.fetched = false;
    }
  }
});

export const currentFileSelector = (state) => state.files.current;
export const draftFileSelector = (state) =>
  getDraftFileSession() ? getDraftFileSession() : state.files.draft;
export const draftFileLogoUrlSelector = (state) =>
  draftFileSelector(state) && draftFileSelector(state).publicUrl
    ? draftFileSelector(state).publicUrl
    : '';
export const isProcessingSelector = (state) => state.files.isProcessing;
export const fileLoadingSelector = (state) => state.files.loading;
export const fileErrorSelector = (state) => state.files.error;
export const fileFetchedSelector = (state) => state.files.fetched;
export const currentFileLogoUrlSelector = (state) =>
  currentFileSelector(state) && currentFileSelector(state).publicUrl
    ? currentFileSelector(state).publicUrl
    : '';

export const {
  fileActionRequest,
  fileActionFailure,
  getCompanyLogoFileSucceeded,
  createFileForUserSucceeded,
  fileIsStartProcessed,
  fileIsEndProcessed,
  createFileForUserFailed,
  currentFileUpdated,
  updateDraftFileFromSessionSucceeded,
  fileDeleted,
  fileDeletedSucceeded
} = filesSlice.actions;

export default filesSlice.reducer;

// Thunk action to find file by user ID
export const findFileByUserId =
  ({ userId, isPublic = false }) =>
  async (dispatch) => {
    console.log('🔍 [File Thunk] Fetching file for userId:', userId);
    
    dispatch(fileActionRequest());
    
    try {
      const userIdEncoded = encodeURIComponent(userId);
      const url = `${BASE_END_POINT_FILE}${FILE_END_POINT}${GET_USER_FILE_END_POINT}/${userIdEncoded}`;
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only add authorization header if not a public request
      if (!isPublic) {
        const localAuth = getLocalStorageAuthentication();
        if (localAuth && localAuth.token) {
          headers.Authorization = `Bearer ${localAuth.token}`;
        }
      }
      
      console.log('🌐 [File Thunk] GET request to:', url);
      console.log('🔒 [File Thunk] Public request:', isPublic);
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('ℹ️ [File Thunk] No file found (404)');
          dispatch(fileActionFailure({ error: null })); // Not really an error
          return { success: true, data: null };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('✅ [File Thunk] File fetched successfully:', data);
      
      // Only dispatch to Redux if NOT a public request
      // Public requests use data directly from the hook
      if (!isPublic) {
        // Dispatch file to Redux store
        const file = Array.isArray(data?.data) && data.data.length > 0
          ? data.data[0]
          : data?.data;
        if (file && file.id) {
          console.log('🖼️ [File Thunk] Dispatching file to Redux');
          dispatch(getCompanyLogoFileSucceeded({ file }));
        }
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('❌ [File Thunk] Failed to fetch file:', error);
      
      dispatch(fileActionFailure({ error: error.message }));
      
      throw error;
    }
  };

// Thunk action to upload file
export const uploadFile =
  ({ file }) =>
  async (dispatch) => {
    console.log('🔍 [Upload File Thunk] Uploading file:', file.name);
    
    dispatch(fileIsStartProcessed());
    dispatch(
      messageUpdated({
        text: 'Uploading file...',
        severity: Severity.INFO
      })
    );
    
    try {
      const localAuth = getLocalStorageAuthentication();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', localAuth.userId);
      
      const url = `${BASE_END_POINT_FILE}${FILE_END_POINT}${UPLOAD_FILE_END_POINT}`;
      
      console.log('🌐 [Upload File Thunk] POST request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localAuth.token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const uploadedFile = data?.data;
      const isFileCreated = uploadedFile && uploadedFile.id;
      
      if (!isFileCreated) {
        throw new Error('File not created');
      }
      
      console.log('✅ [Upload File Thunk] File uploaded successfully:', uploadedFile);
      
      dispatch(fileIsEndProcessed());
      dispatch(
        createFileForUserSucceeded({
          file: uploadedFile
        })
      );
      dispatch(
        messageUpdated({
          text: 'File created successfully',
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true, data: uploadedFile };
    } catch (error) {
      console.error('❌ [Upload File Thunk] Failed:', error);
      
      dispatch(fileIsEndProcessed());
      dispatch(
        messageUpdated({
          text: 'File not created, please try again',
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };

// Thunk action to delete file
export const deleteFile =
  ({ fileId }) =>
  async (dispatch) => {
    console.log('🔍 [Delete File Thunk] Deleting file:', fileId);
    
    dispatch(fileIsStartProcessed());
    dispatch(
      messageUpdated({
        text: 'Deleting file...',
        severity: Severity.INFO
      })
    );
    
    try {
      const localAuth = getLocalStorageAuthentication();
      const url = `${BASE_END_POINT_FILE}${FILE_END_POINT}/${encodeURIComponent(fileId)}`;
      
      console.log('🌐 [Delete File Thunk] DELETE request to:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const file = data?.data;
      const isExistingFile = file && file.id;
      
      if (isExistingFile) {
        throw new Error('File not deleted');
      }
      
      console.log('✅ [Delete File Thunk] File deleted successfully');
      
      dispatch(fileIsEndProcessed());
      dispatch(fileDeletedSucceeded());
      dispatch(
        messageUpdated({
          text: 'File deleted successfully',
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true };
    } catch (error) {
      console.error('❌ [Delete File Thunk] Failed:', error);
      
      dispatch(fileIsEndProcessed());
      dispatch(
        messageUpdated({
          text: 'File not deleted, please try again',
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };
